/**
 * SharedAudioPlayer.tsx - Play-only audio player for the public share view.
 *
 * Given an already-generated, signed audio URL, renders a play/pause button, a
 * progress slider and a time readout. No generation, no auth, no TTS config —
 * unknown viewers can only play audio the owner already generated.
 */

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, HStack, IconButton, Text } from '@chakra-ui/react';
import { LuPlay, LuPause } from 'react-icons/lu';

interface SharedAudioPlayerProps {
    audioUrl: string;
    duration?: number;
}

const formatTime = (time: number): string => {
    if (!isFinite(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const SharedAudioPlayer: React.FC<SharedAudioPlayerProps> = ({ audioUrl, duration: initialDuration }) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const uniqueId = useId();
    const progressBarClass = `shared-audio-progress-${uniqueId.replace(/:/g, '')}`;

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(initialDuration || 0);
    const [isSeeking, setIsSeeking] = useState(false);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleTimeUpdate = () => { if (!isSeeking) setCurrentTime(audio.currentTime); };
        const handleLoadedMetadata = () => setDuration(audio.duration);
        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(audio.duration);
        };

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('ended', handleEnded);
        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [isSeeking]);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
        } else {
            if (audio.duration && audio.currentTime >= audio.duration) {
                audio.currentTime = 0;
                setCurrentTime(0);
            }
            const p = audio.play();
            if (p !== undefined) {
                p.then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
            } else {
                setIsPlaying(true);
            }
        }
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => setCurrentTime(parseFloat(e.target.value));
    const handleSeekEnd = (e: React.SyntheticEvent<HTMLInputElement>) => {
        const audio = audioRef.current;
        if (!audio) return;
        const newTime = parseFloat(e.currentTarget.value);
        audio.currentTime = newTime;
        setCurrentTime(newTime);
        setIsSeeking(false);
    };

    const progressPercentage = useMemo(() => {
        if (!duration) return 0;
        return Math.min(100, Math.max(0, (currentTime / duration) * 100));
    }, [currentTime, duration]);

    return (
        <Box width="100%" paddingY="2">
            <audio ref={audioRef} src={audioUrl} preload="metadata" playsInline />

            <HStack gap="2" width="100%">
                <IconButton
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                    onClick={togglePlay}
                    size="sm"
                    variant="ghost"
                    color="var(--cair-accent)"
                    background="transparent"
                    _hover={{ background: 'var(--cair-accent-subtle)' }}
                >
                    {isPlaying ? <LuPause /> : <LuPlay />}
                </IconButton>

                <Box flex="1" position="relative">
                    <style>{`
                        .${progressBarClass} {
                            -webkit-appearance: none;
                            appearance: none;
                            width: 100%;
                            height: 4px;
                            background: transparent;
                            border-radius: 2px;
                            outline: none;
                            cursor: pointer;
                            direction: ltr;
                        }
                        .${progressBarClass}::-webkit-slider-runnable-track {
                            width: 100%;
                            height: 4px;
                            background: linear-gradient(
                                to right,
                                var(--cair-accent) 0%,
                                var(--cair-accent) ${progressPercentage}%,
                                #e2e8f0 ${progressPercentage}%,
                                #e2e8f0 100%
                            );
                            border-radius: 2px;
                        }
                        .${progressBarClass}::-moz-range-track {
                            width: 100%;
                            height: 4px;
                            background: #e2e8f0;
                            border-radius: 2px;
                        }
                        .${progressBarClass}::-webkit-slider-thumb {
                            -webkit-appearance: none;
                            appearance: none;
                            width: 12px;
                            height: 12px;
                            background: var(--cair-accent);
                            border-radius: 50%;
                            cursor: pointer;
                            margin-top: -4px;
                        }
                        .${progressBarClass}::-moz-range-thumb {
                            width: 12px;
                            height: 12px;
                            background: var(--cair-accent);
                            border: none;
                            border-radius: 50%;
                            cursor: pointer;
                        }
                    `}</style>
                    <input
                        type="range"
                        className={progressBarClass}
                        min={0}
                        max={duration || 100}
                        step={0.01}
                        value={currentTime}
                        onMouseDown={() => setIsSeeking(true)}
                        onTouchStart={() => setIsSeeking(true)}
                        onChange={handleSeek}
                        onMouseUp={handleSeekEnd}
                        onTouchEnd={handleSeekEnd}
                    />
                </Box>

                <Text fontSize="xs" color="gray.500" minWidth="45px">
                    {formatTime(currentTime)} / {formatTime(duration)}
                </Text>
            </HStack>
        </Box>
    );
};

export default SharedAudioPlayer;
